import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field, { Validators } from "components/Field";
import { FieldValidator } from "final-form";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IRegexValidation,
  IRegexValidationSaveRequest,
} from "services/KnowfullyAdminClient";

const { Form } = withTypes<IRegexValidationSaveRequest>();

type RegexValidationFormProps = {
  regexValidation?: IRegexValidation;
  title: string;
  submitLabel: string;
  onSubmit: (values: IRegexValidationSaveRequest) => void;
  onCancel: () => void;
};

const RegexValidationForm = ({
  regexValidation,
  title,
  submitLabel,
  onCancel,
  onSubmit,
}: RegexValidationFormProps &
  FormProps<IRegexValidationSaveRequest>) => {
  const { formatMessage: t } = useIntl();

  const init = useMemo(() => regexValidation ?? {}, [regexValidation]);

  // A pattern pasted as a JavaScript regex literal keeps its delimiters, and the stored value is
  // applied verbatim by the browser, so the rule then matches nothing at all. Three shapes turn up -
  // a leading delimiter before an anchor, the whole literal, and a surviving tail after an end
  // anchor - and none of them catch a slash that is genuinely part of the pattern, so "/users/",
  // "/users/\d+" and "^/users" are all left alone, and so is an escaped dollar, which is a literal
  // "$" rather than an end anchor.
  // Written without lookbehind on purpose: an unsupported group in a regex literal is a syntax error
  // when the file is parsed, which would take the whole page down rather than one field.
  // Kept character for character the same as the check the service makes.
  const jsRegexLiteralShape = /^\/\^|^\/.*\/[dgimsuvy]+$|(^|[^\\])\$\/[dgimsuvy]+$/;
  const notDelimited: FieldValidator<string> = (value) =>
    value && jsRegexLiteralShape.test(value)
      ? t({ id: "regexValidationDelimiterError" })
      : undefined;

  // This is the engine that will actually run the pattern, so it is the one that decides whether a
  // pattern is usable. A rule that cannot compile breaks the profile form for every learner who
  // holds the credential it is attached to.
  const compilable: FieldValidator<string> = (value) => {
    if (!value) return undefined;
    try {
      // eslint-disable-next-line no-new
      new RegExp(value);
      return undefined;
    } catch {
      return t({ id: "regexValidationInvalidPatternError" });
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle>{title}</DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "regexValidationNameLabel" })}
                      InputLabelProps={{ required: true }}
                      error={!!meta.error && meta.submitFailed}
                      helperText={meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field
                name="value"
                required
                customValidator={Validators.composeValidators(
                  notDelimited,
                  compilable,
                )}
              >
                {({ input, meta }) => {
                  const showError =
                    !!meta.error && (meta.dirty || meta.submitFailed);
                  return (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "regexValidationValueLabel" })}
                        InputLabelProps={{ required: true }}
                        error={showError}
                        helperText={showError && meta.error}
                        inputProps={{ style: { fontFamily: "monospace" } }}
                        {...input}
                      />
                    </Grid>
                  );
                }}
              </Field>

              <Field name="message" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      multiline
                      rows={2}
                      label={t({ id: "regexValidationMessageLabel" })}
                      InputLabelProps={{ required: true }}
                      error={!!meta.error && meta.submitFailed}
                      helperText={meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Grid item xs={12}>
                  <Alert severity="error">{submitError}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button type="submit" color="primary" disabled={submitting}>
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default RegexValidationForm;
